/** Static, authenticated app destinations available to the GA page catalog. */
export interface PageCatalogSettingsPermissions {
  canManageBilling: boolean;
  canViewBilling: boolean;
  canViewStorageLimits: boolean;
  showFilePermissions: boolean;
}

export type PageDefinition = {
  id: string;
  label: string;
  route: string;
  searchTerms: readonly string[];
};

export const WORKSPACE_PAGES: readonly PageDefinition[] = [
  {
    id: 'workspace-home',
    label: 'Workspace home',
    route: '',
    searchTerms: ['home', 'dashboard', 'start'],
  },
  {
    id: 'threads',
    label: 'Chats',
    route: '/threads',
    searchTerms: ['threads', 'conversations', 'chat history'],
  },
  {
    id: 'work-chats',
    label: 'Work chats',
    route: '/chats',
    searchTerms: ['channels', 'team chat', 'inbox'],
  },
  {
    id: 'scheduled',
    label: 'Scheduled tasks',
    route: '/scheduled',
    searchTerms: ['automations', 'recurring tasks', 'schedule'],
  },
  {
    id: 'products',
    label: 'Products',
    route: '/products',
    searchTerms: ['my products', 'product studio'],
  },
  {
    id: 'new-product',
    label: 'New product',
    route: '/products/add',
    searchTerms: ['add product', 'create product'],
  },
  {
    id: 'design',
    label: 'Design',
    route: '/design',
    searchTerms: ['create', 'design studio'],
  },
  {
    id: 'studio',
    label: 'Studio',
    route: '/studio',
    searchTerms: ['lookbooks', 'creative studio'],
  },
  {
    id: 'aesthetics',
    label: 'Aesthetics',
    route: '/aesthetics',
    searchTerms: ['styles', 'moodboards'],
  },
  {
    id: 'explore',
    label: 'Explore',
    route: '/explore',
    searchTerms: ['discover', 'inspiration'],
  },
  {
    id: 'references',
    label: 'References',
    route: '/references',
    searchTerms: ['reference images', 'inspiration library'],
  },
  {
    id: 'documents',
    label: 'Documents',
    route: '/documents',
    searchTerms: ['docs', 'document library'],
  },
  {
    id: 'projects',
    label: 'Projects',
    route: '/projects',
    searchTerms: ['project folders'],
  },
  {
    id: 'library',
    label: 'Library',
    route: '/library',
    searchTerms: ['asset library', 'saved media'],
  },
  {
    id: 'media-files',
    label: 'Media files',
    route: '/files/media',
    searchTerms: ['media library', 'images', 'videos'],
  },
  {
    id: 'product-files',
    label: 'Product files',
    route: '/files/products',
    searchTerms: ['product folders', 'product files'],
  },
  {
    id: 'avatars',
    label: 'Avatars',
    route: '/files/avatars',
    searchTerms: ['avatar files', 'models', 'characters'],
  },
  {
    id: 'new-avatar',
    label: 'New avatar',
    route: '/avatars/new',
    searchTerms: ['create avatar', 'add avatar'],
  },
  {
    id: 'brands',
    label: 'Brands',
    route: '/files/brands',
    searchTerms: ['brand', 'brand files', 'brand folders', 'branding'],
  },
  {
    id: 'workspace-files',
    label: 'Workspace files',
    route: '/files',
    searchTerms: ['file browser', 'files', 'folders'],
  },
  {
    id: 'shared-files',
    label: 'Shared files',
    route: '/files/shared-with-me',
    searchTerms: ['files shared with me', 'file shares'],
  },
  {
    id: 'user-homes',
    label: 'User homes',
    route: '/files/users',
    searchTerms: ['home folders', 'users files'],
  },
  {
    id: 'drives',
    label: 'Drives',
    route: '/drives',
    searchTerms: ['shared drives', 'team drives'],
  },
  {
    id: 'workspace-library-drive',
    label: 'Workspace library drive',
    route: '/drives/library',
    searchTerms: ['library drive', 'workspace library'],
  },
  {
    id: 'shared-with-me',
    label: 'Shared with me',
    route: '/drives/shared-with-me',
    searchTerms: ['shared files', 'incoming shares'],
  },
  {
    id: 'team-drives',
    label: 'Team drives',
    route: '/drives/teams',
    searchTerms: ['team files', 'shared team folders'],
  },
  {
    id: 'project-drives',
    label: 'Project drives',
    route: '/drives/projects',
    searchTerms: ['project files', 'project folders'],
  },
  {
    id: 'connectors',
    label: 'Connectors',
    route: '/connectors',
    searchTerms: ['integrations', 'connected apps'],
  },
  {
    id: 'downloads',
    label: 'Downloads',
    route: '/downloads',
    searchTerms: ['downloaded files', 'exports'],
  },
  {
    id: 'trash',
    label: 'Trash',
    route: '/trash',
    searchTerms: ['deleted files', 'recycle bin'],
  },
  {
    id: 'support',
    label: 'Support',
    route: '/support',
    searchTerms: ['help', 'tickets'],
  },
  {
    id: 'trend-analysis',
    label: 'Trend analysis',
    route: '/trend-analysis',
    searchTerms: ['trends', 'market research'],
  },
  {
    id: 'branding',
    label: 'Branding',
    route: '/branding',
    searchTerms: ['brand studio', 'brand analysis'],
  },
  {
    id: 'brand-time-machine',
    label: 'Brand time machine',
    route: '/branding/time-machine',
    searchTerms: ['brand history', 'brand timeline'],
  },
];

export const PERSONAL_SETTINGS: readonly PageDefinition[] = [
  {
    id: 'shared',
    label: 'Shared with me',
    route: '/shared',
    searchTerms: ['shared items', 'shared links'],
  },
  {
    id: 'personal-settings',
    label: 'Personal settings',
    route: '/settings/profile',
    searchTerms: ['account settings', 'profile', 'my settings'],
  },
  {
    id: 'personal-memories',
    label: 'Personal memories',
    route: '/settings/memories',
    searchTerms: ['my memories', 'personalization'],
  },
  {
    id: 'personal-summarization',
    label: 'Summarization settings',
    route: '/settings/summarization',
    searchTerms: ['summary', 'voice', 'speech'],
  },
  {
    id: 'appearance',
    label: 'Appearance settings',
    route: '/settings/appearance',
    searchTerms: ['theme', 'dark mode', 'language', 'haptics'],
  },
  {
    id: 'security',
    label: 'Security settings',
    route: '/settings/security',
    searchTerms: ['password', 'authentication', 'login'],
  },
  {
    id: 'my-workspaces',
    label: 'My workspaces',
    route: '/settings/organizations',
    searchTerms: ['organizations', 'switch workspace'],
  },
  {
    id: 'my-invitations',
    label: 'My invitations',
    route: '/settings/invitations',
    searchTerms: ['invites', 'join workspace'],
  },
];

export const WORKSPACE_SETTINGS: readonly (PageDefinition & {
  permission?: keyof PageCatalogSettingsPermissions;
})[] = [
  {
    id: 'workspace-settings',
    label: 'Workspace settings',
    route: '/settings/workspace',
    searchTerms: ['organization settings', 'workspace details', 'name', 'logo'],
  },
  {
    id: 'members',
    label: 'Members',
    route: '/settings/members',
    searchTerms: ['people', 'team members', 'invite members'],
  },
  {
    id: 'teams',
    label: 'Teams',
    route: '/settings/teams',
    searchTerms: ['groups', 'team access'],
  },
  {
    id: 'roles',
    label: 'Roles',
    route: '/settings/roles',
    searchTerms: ['permissions', 'access control', 'member role'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    route: '/settings/analytics',
    searchTerms: ['activity', 'insights', 'metrics', 'reports'],
  },
  {
    id: 'workspace-memories',
    label: 'Workspace memories',
    route: '/settings/memories',
    searchTerms: ['shared knowledge', 'workspace context'],
  },
  {
    id: 'file-permissions',
    label: 'File permissions',
    route: '/settings/file-permissions',
    searchTerms: ['filesystem permissions', 'folder access'],
    permission: 'showFilePermissions',
  },
  {
    id: 'storage',
    label: 'Storage settings',
    route: '/settings/storage',
    searchTerms: ['storage limits', 'files used'],
    permission: 'canViewStorageLimits',
  },
  {
    id: 'plans',
    label: 'Plans and credits',
    route: '/settings/purchase',
    searchTerms: ['pricing', 'subscription', 'top up', 'upgrade'],
    permission: 'canManageBilling',
  },
  {
    id: 'usage',
    label: 'Usage',
    route: '/settings/usage',
    searchTerms: ['credits used', 'usage history', 'spending'],
    permission: 'canViewBilling',
  },
  {
    id: 'billing',
    label: 'Billing',
    route: '/settings/billing',
    searchTerms: ['invoice', 'payment', 'receipt'],
    permission: 'canViewBilling',
  },
  {
    id: 'archived-chats',
    label: 'Archived chats',
    route: '/settings/archived-chats',
    searchTerms: ['archive', 'restore chats'],
  },
];
